/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Button, InputGroup } from 'react-bootstrap';
import { capitalize, isEqual } from 'lodash';
import { Roles } from 'meteor/alanning:roles';
import { Bert } from 'meteor/themeteorchef:bert';

import * as userOps from '../../../api/Users/user-operations';

import methodPromise from '../../../modules/method-promise-wrapper';

import Validation from '../../containers/Validation';

import Styles from './styles';

const OAuthUser = ({ user }) => {
  const userService = userOps.getOauthService(user);
  const existingUser = userOps.getId(user);
  if (existingUser && userOps.isOauthUser(user)) {
    return (
      <Styles.Profile>
        <div key={userService} className={`LoggedInWith ${userService}`}>
          <img src={`/${userService}.svg`} alt={userService} />
          <p>{`This user is logged in with ${capitalize(
            userService
          )} using the email address ${userOps.getEmailAddress(user)}.`}</p>
        </div>
      </Styles.Profile>
    );
  }
  return '';
};

OAuthUser.propTypes = {
  user: PropTypes.object.isRequired
};

const PasswordUser = ({ user, handleResetPassword }) => {
  const existingUser = userOps.getId(user);
  if (!existingUser || userOps.isPasswordUser(user)) {
    return (
      <>
        <Form.Row>
          <Form.Group as={Col} xs={6}>
            <Form.Label>First Name</Form.Label>
            <Form.Control type="text" name="firstName" defaultValue={userOps.getFirstName(user)} />
          </Form.Group>
          <Form.Group as={Col} xs={6}>
            <Form.Label>Last Name</Form.Label>
            <Form.Control type="text" name="lastName" defaultValue={userOps.getLastName(user)} />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} xs={6}>
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              name="emailAddress"
              defaultValue={userOps.getEmailAddress(user)}
            />
          </Form.Group>
          <Form.Group as={Col} xs={6}>
            <Form.Label>Password</Form.Label>
            <Form.Row className="justify-content-start align-items-middle">
              <Col>
                <Form.Text>
                  The new password will be <strong>password</strong>
                </Form.Text>
              </Col>
              {existingUser && (
                <Col>
                  <Button variant="warning" onClick={() => handleResetPassword(existingUser)}>
                    Reset Password
                  </Button>
                </Col>
              )}
            </Form.Row>
          </Form.Group>
        </Form.Row>
        <Button type="submit" variant="success">
          {existingUser ? 'Save Changes' : 'Add User'}
        </Button>
        <hr />
      </>
    );
  }
  return '';
};

PasswordUser.propTypes = {
  user: PropTypes.object.isRequired,
  handleResetPassword: PropTypes.func.isRequired
};

const RoleAndStorage = ({ user, userCuota, userRoles, handleChangeUserRole, setStorageSize }) => {
  const existingUser = userOps.getId(user);
  const sizeLimitMB = userCuota.sizeLimit / 10 ** 6;
  const storageUsedMB = userCuota.usedStorage / 10 ** 6;
  if (existingUser) {
    return (
      <Form.Row>
        <Form.Group as={Col} xs={12} sm={6}>
          <Form.Label>User Type</Form.Label>
          <br />
          <Form.Check
            inline
            title="free-user"
            defaultChecked={userRoles.freeUser}
            onChange={e => handleChangeUserRole(e, 'free-user')}
            label=" Free User"
          />
          <Form.Check
            inline
            title="basic"
            defaultChecked={userRoles.basicUser}
            onChange={e => handleChangeUserRole(e, 'basic-user')}
            label="Basic"
          />

          <Form.Check
            inline
            title="premium-user"
            defaultChecked={userRoles.premiumUser}
            onChange={e => handleChangeUserRole(e, 'premium-user')}
            label="Premium"
          />

          <Form.Check
            inline
            defaultChecked={userRoles.isAdmin}
            onChange={e => handleChangeUserRole(e, 'admin')}
            label={<strong>Is Admin?</strong>}
          />
        </Form.Group>
        <Form.Group as={Col} xs={12} sm={6}>
          <Form.Label>Storage Cuota (Used: {storageUsedMB} MB)</Form.Label>
          <InputGroup>
            <Form.Control
              type="number"
              min="0"
              name="storageSize"
              defaultValue={sizeLimitMB}
              onChange={setStorageSize}
            />
            <InputGroup.Append>
              <InputGroup.Text>MB</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Form.Group>
      </Form.Row>
    );
  }
  return '';
};

RoleAndStorage.propTypes = {
  user: PropTypes.object.isRequired,
  userCuota: PropTypes.object.isRequired,
  userRoles: PropTypes.object.isRequired,
  handleChangeUserRole: PropTypes.func.isRequired,
  setStorageSize: PropTypes.func.isRequired
};

class UserEditor extends Component {
  state = {
    userRoles: {
      freeUser: Roles.userIsInRole(userOps.getId(this.props.user), ['free-user']),
      basicUser: Roles.userIsInRole(userOps.getId(this.props.user), ['basic-user']),
      premiumUser: Roles.userIsInRole(userOps.getId(this.props.user), ['premium-user']),
      isAdmin: Roles.userIsInRole(userOps.getId(this.props.user), ['admin'])
    },
    userCuota: {
      sizeLimit: userOps.getSizeLimit(this.props.user),
      usedStorage: userOps.getUsedStorage(this.props.user)
    }
  };

  componentDidUpdate(prevProps) {
    const { user } = this.props;
    const { user: prevUser } = prevProps;
    if (!isEqual(user, prevUser)) {
      this.updateUserState();
    }
  }

  updateUserState = () => {
    const { user } = this.props;
    this.setState({
      userRoles: {
        freeUser: Roles.userIsInRole(userOps.getId(user), ['free-user']),
        premiumUser: Roles.userIsInRole(userOps.getId(user), ['premium-user']),
        isAdmin: Roles.userIsInRole(userOps.getId(user), ['admin'])
      },
      userCuota: {
        sizeLimit: userOps.getSizeLimit(user),
        usedStorage: userOps.getUsedStorage(user)
      }
    });
  };

  setStorageSize = async event => {
    const { user } = this.props;
    const newStorage = Number(event.target.value.trim());
    const storageInBytes = newStorage * 10 ** 6;
    try {
      await methodPromise('users.changeStorageLimit', userOps.getId(user), storageInBytes);
      Bert.alert('User Storage Updated', 'success');
    } catch (error) {
      Bert.alert(`Something went wrong ${error.message}`, 'danger');
    }
  };

  handleResetPassword = async userId => {
    try {
      await methodPromise('users.setPassword', userId);
      Bert.alert('User password reset to (password)', 'success');
    } catch (error) {
      Bert.alert(error.message, 'danger');
    }
  };

  handleSubmit = async form => {
    const { history, user, deployConfig } = this.props;
    const existingUser = userOps.getId(user);
    const methodToCall = existingUser ? 'users.update' : 'users.insert';
    const newUser = {
      email: form.emailAddress.value,
      role: deployConfig.DefaultConfig.DefaultRole,
      profile: {
        name: {
          first: form.firstName.value,
          last: form.lastName.value
        }
      }
    };
    if (existingUser) {
      newUser._id = existingUser;
    } else {
      newUser.password = 'password';
      newUser.role = deployConfig.DefaultConfig.DefaultRole;
    }
    try {
      await methodPromise(methodToCall, newUser);
      const confirmation = existingUser ? 'User updated!' : 'User added!';
      form.reset();
      Bert.alert(confirmation, 'success');
      history.push('/users');
    } catch (error) {
      Bert.alert(error.message, 'danger');
    }
  };

  handleChangeUserRole = async (event, userRole) => {
    const { user } = this.props;
    const isChecked = event.target.checked;
    try {
      await methodPromise('users.changeRole', userOps.getId(user), userRole, isChecked);
      Bert.alert('User Role Updated', 'success');
    } catch (error) {
      Bert.alert(error.message, 'danger');
    }
  };

  render() {
    const { user } = this.props;
    const { userCuota, userRoles } = this.state;
    const validationProps = {
      rules: {
        firstName: {
          required: true
        },
        lastName: {
          required: true
        },
        emailAddress: {
          required: true,
          email: true
        }
      },
      messages: {
        firstName: {
          required: 'What\u0027s your first name?'
        },
        lastName: {
          required: 'What\u0027s your last name?'
        },
        emailAddress: {
          required: 'Need an email address here.',
          email: 'Is this email address correct?'
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };
    return (
      <>
        <Validation {...validationProps}>
          <Form onSubmit={event => event.preventDefault()}>
            <OAuthUser user={user} />
            <PasswordUser user={user} handleResetPassword={this.handleResetPassword} />
          </Form>
        </Validation>
        <br />
        <RoleAndStorage
          user={user}
          userCuota={userCuota}
          userRoles={userRoles}
          handleChangeUserRole={this.handleChangeUserRole}
          setStorageSize={this.setStorageSize}
        />
      </>
    );
  }
}

UserEditor.defaultProps = {
  user: {
    email: '',
    profile: {
      name: {
        first: '',
        last: ''
      }
    }
  }
};

UserEditor.propTypes = {
  history: PropTypes.object.isRequired,
  deployConfig: PropTypes.object.isRequired,
  user: PropTypes.object
};

export default UserEditor;
